// ViewSelectorBar.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    buildViewSelectorOptions,
    getActiveSelectorLabel,
    getActiveSelectorOption,
} from 'utilities/ViewSelectorOptions';
import {
    bucketViewsByGroup,
    hasConfiguredViewGroups,
    resolveActiveViewGroupId,
} from 'utilities/ViewGroups';
import { getDefaultViewTypeIconKey } from 'utilities/ViewTypeIcons';

// CSS
import './ViewSelectorBar.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Popup from 'components/popup/Popup';
import ViewsPopupContents from 'node/views/popup/ViewsPopupContents';

/**
 * ViewGroupDropdown
 *
 * One group trigger + popup of views in that group.
 */
const ViewGroupDropdown = React.memo(({
    label,
    views,
    activeViewId,
    isActiveGroup,
    includeAddOption,
    theme,
    onChangeView,
    onAddView,
    onEditView,
    onOpenEditor,
}) => {
    const anchorRef = useRef(null);
    const [popupOpen, setPopupOpen] = useState(false);

    const options = useMemo(() => {
        return buildViewSelectorOptions(views, {
            fallbackType: 'TABLE',
            includeAddOption,
        });
    }, [includeAddOption, views]);

    // Active group shows the selected view title; inactive groups keep the group label.
    const triggerLabel = useMemo(() => {
        if (!isActiveGroup) return label;
        return getActiveSelectorLabel(options, activeViewId, label);
    }, [activeViewId, isActiveGroup, label, options]);

    const handleSelect = (optionId) => {
        setPopupOpen(false);
        if (optionId === '__add_view__') {
            onAddView?.();
            return;
        }
        onChangeView?.(optionId);
    };

    const handleEdit = (optionId) => {
        if (typeof onEditView !== 'function') return;
        setPopupOpen(false);
        const option = options.find((nextOption) => nextOption.id === optionId);
        if (!option?.view) return;
        onEditView(option.view);
        onOpenEditor?.();
    };

    return (
        <>
            <div
                className={`view-selector-bar-trigger${isActiveGroup ? ' is-active' : ''}`}
                ref={anchorRef}
                title={triggerLabel}
                onClick={() => setPopupOpen((prev) => !prev)}
            >
                <div
                    className="view-selector-bar-trigger-text"
                    style={{
                        color: isActiveGroup ? theme.foreground : theme.foregroundMuted,
                        fontWeight: isActiveGroup ? 600 : 400,
                    }}
                >
                    {triggerLabel}
                </div>
                <Icon
                    iconKey="keyboard_arrow_down"
                    width={18}
                    height={18}
                    color={theme.foregroundMuted}
                />
            </div>

            <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>
                <ViewsPopupContents
                    options={options}
                    selectedViewId={activeViewId}
                    onEditOption={typeof onEditView === 'function' ? handleEdit : undefined}
                    onSelectOption={handleSelect}
                />
            </Popup>
        </>
    );
});

ViewGroupDropdown.displayName = 'ViewGroupDropdown';

/**
 * ViewSelectorBar
 *
 * Shared popup selector for saved views across database and node surfaces.
 * When the type has view groups, renders one dropdown per group (+ Unsorted).
 *
 * @returns {ReactNode} The rendered view selector.
 */
const ViewSelectorBar = React.memo(({
    views = [],
    viewGroups = [],
    activeViewId,
    onChangeView,
    onAddView,
    onEditView,
    onOpenEditor,
    showLabel = false,
    fallbackLabel = 'Saved Views',
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- REFS ----- //

    const flatAnchorRef = useRef(null);

    // ----- STATE ----- //

    const [popupOpen, setPopupOpen] = useState(false);

    // ----- MEMOS ----- //

    const useGroupedChrome = useMemo(
        () => hasConfiguredViewGroups(viewGroups),
        [viewGroups],
    );

    // Configured groups only — unsorted/global views render as chips to the right.
    const groupColumns = useMemo(() => {
        if (!useGroupedChrome) return [];
        const { groups } = bucketViewsByGroup(views, viewGroups);
        return groups
            .filter((group) => group.views.length > 0)
            .map((group) => ({
                id: group.id,
                label: group.label,
                views: group.views,
            }));
    }, [useGroupedChrome, viewGroups, views]);

    const activeGroupId = useMemo(() => {
        if (!useGroupedChrome) return null;
        return resolveActiveViewGroupId(views, viewGroups, activeViewId);
    }, [activeViewId, useGroupedChrome, viewGroups, views]);

    const flatOptions = useMemo(() => {
        return buildViewSelectorOptions(views, {
            fallbackType: 'TABLE',
            includeAddOption: typeof onAddView === 'function',
        });
    }, [onAddView, views]);

    const activeViewLabel = useMemo(() => {
        return getActiveSelectorLabel(flatOptions, activeViewId, fallbackLabel);
    }, [activeViewId, fallbackLabel, flatOptions]);

    const popupButtonTitle = activeViewLabel || fallbackLabel;

    const activeViewIconKey = useMemo(() => {
        const match = getActiveSelectorOption(flatOptions, activeViewId);
        return getDefaultViewTypeIconKey(match?.type) || 'layers';
    }, [activeViewId, flatOptions]);

    // ----- HANDLERS ----- //

    const handleChange = (nextValue) => {
        if (nextValue === '__add_view__') {
            onAddView?.();
            return;
        }
        onChangeView?.(nextValue);
    };

    const handlePopupSelect = (optionId) => {
        setPopupOpen(false);
        handleChange(optionId);
    };

    const handlePopupEdit = (optionId) => {
        if (typeof onEditView !== 'function') return;
        setPopupOpen(false);
        const option = flatOptions.find((nextOption) => nextOption.id === optionId);
        if (!option?.view) return;
        onEditView(option.view);
        onOpenEditor?.();
    };

    // ----- RENDER ----- //

    if (useGroupedChrome) {
        if (groupColumns.length === 0) return null;

        const lastId = groupColumns[groupColumns.length - 1]?.id;
        return (
            <div className="view-selector-bar-container view-selector-bar-container--groups">
                {groupColumns.map((column) => (
                    <ViewGroupDropdown
                        key={column.id}
                        label={column.label}
                        views={column.views}
                        activeViewId={activeViewId}
                        isActiveGroup={activeGroupId === column.id}
                        includeAddOption={typeof onAddView === 'function' && column.id === lastId}
                        theme={theme}
                        onChangeView={onChangeView}
                        onAddView={onAddView}
                        onEditView={onEditView}
                        onOpenEditor={onOpenEditor}
                    />
                ))}
            </div>
        );
    }

    return (
        <>
            <div className="view-selector-bar-container">
                <div
                  className="view-selector-bar-trigger"
                  ref={flatAnchorRef}
                  title={popupButtonTitle}
                  onClick={() => setPopupOpen((prev) => !prev)}
                >
                    <Icon
                      iconKey={activeViewIconKey}
                      width={20}
                      height={20}
                      color={activeViewId ? theme.foreground : theme.foregroundMuted}
                    />

                    {showLabel ? (
                        <div
                          className="view-selector-bar-trigger-text"
                          style={{
                                color: activeViewId ? theme.foreground : theme.foregroundMuted,
                            }}
                        >
                            {activeViewLabel}
                        </div>
                    ) : null}

                    <Icon
                      iconKey="keyboard_arrow_down"
                      width={18}
                      height={18}
                      color={theme.foregroundMuted}
                    />
                </div>
            </div>

            <Popup anchorRef={flatAnchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>
                <ViewsPopupContents
                  options={flatOptions}
                  selectedViewId={activeViewId}
                  onEditOption={typeof onEditView === 'function' ? handlePopupEdit : undefined}
                  onSelectOption={handlePopupSelect}
                />
            </Popup>
        </>
    );
});

// Dev Tools
ViewSelectorBar.displayName = 'ViewSelectorBar';

export default ViewSelectorBar;
